import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Columns3, FolderKanban, LayoutGrid, List, Plus, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, CATEGORIES, eur, errMsg, fmtDateShort, PAY_STATUS, PRIORITY, STATUS, STATUS_ORDER } from "../lib/api";
import { EmptyState, PageHeader, PayBadge, PriorityBadge, StatusBadge, UserAvatar } from "../components/ui-bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ProjectForm } from "../components/ProjectForm";

function ProjectCard({ p, draggable, onDragStart, testid }) {
  const navigate = useNavigate();
  return (
    <div
      data-testid={testid || `project-card-${p.id}`}
      draggable={draggable}
      onDragStart={onDragStart}
      onClick={() => navigate(`/projekte/${p.id}`)}
      className="cursor-pointer rounded-2xl border border-[#1D225C]/10 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#B88A4A]/40 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold text-[#B88A4A]">{p.client_name}</p>
          <p className="truncate font-display text-sm font-bold text-[#1D225C]">{p.name}</p>
          {p.website_url && <p className="truncate text-[11px] text-[#999]">{p.website_url}</p>}
        </div>
        {p.unread_comments > 0 && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#B88A4A] px-1 text-[10px] font-bold text-white" title="Ungelesene Kommentare">
            {p.unread_comments}
          </span>
        )}
      </div>
      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        <PriorityBadge priority={p.priority} />
        {p.payment_status !== "keine" && <PayBadge status={p.payment_status} />}
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[#1D225C]/10">
        <div className="h-full rounded-full bg-[#B88A4A]" style={{ width: `${p.progress}%` }} />
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-[#777]">
        <span>{p.progress}% · {p.task_done}/{p.task_total}</span>
        {p.deadline && <span className="font-medium text-[#1D225C]">{fmtDateShort(p.deadline)}</span>}
      </div>
      <div className="mt-2.5 flex items-center justify-between border-t border-[#1D225C]/5 pt-2.5">
        {p.assignee_name ? (
          <span className="flex items-center gap-1.5 text-[11px] text-[#777]">
            <UserAvatar name={p.assignee_name} color={p.assignee_color} avatar={p.assignee_avatar} size={5} />
            {p.assignee_name.split(" ")[0]}
          </span>
        ) : (
          <span className="text-[11px] text-[#999]">Nicht zugewiesen</span>
        )}
        {p.open_payment > 0 && <span className="text-[11px] font-bold text-[#B88A4A]">{eur(p.open_payment)} offen</span>}
      </div>
    </div>
  );
}

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("kanban");
  const [filters, setFilters] = useState({ status: "all", client_id: "all", assigned_to: "all", priority: "all", type: "all", payment_status: "all", sort: "activity", q: "" });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      ["status", "client_id", "assigned_to", "priority", "type", "payment_status"].forEach((k) => {
        if (filters[k] !== "all") params[k] = filters[k];
      });
      params.sort = filters.sort;
      const { data } = await api.get("/projects", { params });
      setProjects(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [c, u] = await Promise.all([api.get("/clients"), api.get("/users")]);
        setClients(c.data);
        setUsers(u.data);
      } catch {}
    })();
  }, []);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const resetFilters = () => setFilters({ status: "all", client_id: "all", assigned_to: "all", priority: "all", type: "all", payment_status: "all", sort: "activity", q: "" });

  const visible = filters.q
    ? projects.filter((p) => `${p.name} ${p.client_name} ${p.website_url}`.toLowerCase().includes(filters.q.toLowerCase()))
    : projects;

  const onDrop = async (e, status) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("text/plain");
    const proj = projects.find((p) => p.id === id);
    if (!proj || proj.status === status) return;
    setProjects((ps) => ps.map((p) => (p.id === id ? { ...p, status } : p)));
    try {
      await api.patch(`/projects/${id}/status`, { status });
      toast.success(`„${proj.name}" → ${STATUS[status].label}`);
    } catch (err) {
      toast.error(errMsg(err));
      load();
    }
  };

  const filterSelects = [
    { k: "status", label: "Status", options: Object.entries(STATUS).map(([v, c]) => ({ v, l: c.label })), testid: "filter-status" },
    { k: "client_id", label: "Kunde", options: clients.map((c) => ({ v: c.id, l: c.company })), testid: "filter-client" },
    { k: "assigned_to", label: "Bearbeiter", options: users.map((u) => ({ v: u.id, l: u.name })), testid: "filter-assignee" },
    { k: "priority", label: "Priorität", options: Object.entries(PRIORITY).map(([v, c]) => ({ v, l: c.label })), testid: "filter-priority" },
    { k: "type", label: "Typ", options: Object.entries(CATEGORIES).map(([v, l]) => ({ v, l })), testid: "filter-type" },
    { k: "payment_status", label: "Zahlung", options: Object.entries(PAY_STATUS).map(([v, c]) => ({ v, l: c.label })), testid: "filter-payment" },
  ];

  return (
    <div className="animate-fade-up" data-testid="projects-page">
      <PageHeader overline="Pipeline" title="Projekte" testid="projects-header">
        <div className="flex rounded-xl border border-[#1D225C]/10 bg-white p-1 shadow-sm">
          {[
            { v: "kanban", icon: Columns3, testid: "view-kanban" },
            { v: "list", icon: List, testid: "view-list" },
            { v: "grid", icon: LayoutGrid, testid: "view-grid" },
          ].map((b) => (
            <button
              key={b.v}
              data-testid={b.testid}
              onClick={() => setView(b.v)}
              className={`rounded-lg p-2 transition-colors ${view === b.v ? "bg-[#1D225C] text-white" : "text-[#1D225C]/50 hover:text-[#1D225C]"}`}
            >
              <b.icon className="h-4 w-4" />
            </button>
          ))}
        </div>
        <Button data-testid="new-project-button" onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-xl bg-[#B88A4A] text-white hover:bg-[#a67a3f]">
          <Plus className="mr-1.5 h-4 w-4" /> Neues Projekt
        </Button>
      </PageHeader>

      <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="projects-filters">
        <Input
          data-testid="filter-search"
          value={filters.q}
          onChange={(e) => setF("q", e.target.value)}
          placeholder="Suchen…"
          className="h-9 w-44 rounded-xl bg-white"
        />
        {filterSelects.map((s) => (
          <Select key={s.k} value={filters[s.k]} onValueChange={(v) => setF(s.k, v)}>
            <SelectTrigger data-testid={s.testid} className="h-9 w-40 rounded-xl bg-white text-xs"><SelectValue placeholder={s.label} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{s.label}: Alle</SelectItem>
              {s.options.map((o) => (
                <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}
        <Select value={filters.sort} onValueChange={(v) => setF("sort", v)}>
          <SelectTrigger data-testid="filter-sort" className="h-9 w-44 rounded-xl bg-white text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="activity">Letzte Aktivität</SelectItem>
            <SelectItem value="newest">Neueste zuerst</SelectItem>
            <SelectItem value="oldest">Älteste zuerst</SelectItem>
            <SelectItem value="deadline">Deadline</SelectItem>
            <SelectItem value="priority">Priorität</SelectItem>
          </SelectContent>
        </Select>
        <button data-testid="filter-reset" onClick={resetFilters} className="flex items-center gap-1 text-xs font-medium text-[#777] hover:text-[#1D225C]">
          <RotateCcw className="h-3.5 w-3.5" /> Zurücksetzen
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">{[...Array(6)].map((_, i) => <div key={i} className="h-40 animate-pulse rounded-2xl bg-white" />)}</div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={FolderKanban}
          title="Keine Projekte gefunden"
          hint={projects.length === 0 ? "Lege zuerst einen Kunden an und erstelle dann dein erstes Projekt." : "Passe die Filter an oder erstelle ein neues Projekt."}
          testid="projects-empty"
        >
          <Button data-testid="empty-new-project-button" onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-xl bg-[#B88A4A] text-white hover:bg-[#a67a3f]">
            <Plus className="mr-1.5 h-4 w-4" /> Projekt erstellen
          </Button>
        </EmptyState>
      ) : view === "kanban" ? (
        <div className="kanban-scroll flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
          {STATUS_ORDER.map((status) => {
            const col = visible.filter((p) => p.status === status);
            return (
              <div
                key={status}
                data-testid={`kanban-column-${status}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(status); }}
                onDragLeave={() => setDragOver((d) => (d === status ? null : d))}
                onDrop={(e) => onDrop(e, status)}
                className={`flex w-64 shrink-0 flex-col rounded-2xl border border-[#1D225C]/10 bg-[#1D225C]/[0.03] p-2.5 transition-colors ${dragOver === status ? "kanban-col-drag" : ""}`}
              >
                <div className="mb-2 flex items-center justify-between px-1.5">
                  <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide" style={{ color: STATUS[status].color }}>
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: STATUS[status].color }} />
                    {STATUS[status].label}
                  </span>
                  <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-[#1D225C] shadow-sm">{col.length}</span>
                </div>
                <div className="flex min-h-24 flex-col gap-2.5">
                  {col.map((p) => (
                    <ProjectCard
                      key={p.id}
                      p={p}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData("text/plain", p.id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : view === "grid" ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="projects-grid">
          {visible.map((p) => <ProjectCard key={p.id} p={p} />)}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[#1D225C]/10 bg-white shadow-sm" data-testid="projects-list">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-[#1D225C]/10 text-left text-xs uppercase tracking-wide text-[#777]">
                <th className="px-4 py-3 font-semibold">Projekt</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Priorität</th>
                <th className="px-4 py-3 font-semibold">Deadline</th>
                <th className="px-4 py-3 font-semibold">Fortschritt</th>
                <th className="px-4 py-3 font-semibold">Zahlung</th>
                <th className="px-4 py-3 font-semibold">Bearbeiter</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`project-row-${p.id}`}
                  onClick={() => navigate(`/projekte/${p.id}`)}
                  className="cursor-pointer border-b border-[#1D225C]/5 transition-colors last:border-0 hover:bg-[#F7F6F2]"
                >
                  <td className="px-4 py-3">
                    <p className="font-semibold text-[#1D225C]">{p.name}</p>
                    <p className="text-xs text-[#777]">{p.client_name}{p.website_url ? ` · ${p.website_url}` : ""}</p>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-3"><PriorityBadge priority={p.priority} /></td>
                  <td className="px-4 py-3 text-xs text-[#1D225C]">{fmtDateShort(p.deadline)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-[#1D225C]/10">
                        <div className="h-full rounded-full bg-[#B88A4A]" style={{ width: `${p.progress}%` }} />
                      </div>
                      <span className="text-xs text-[#777]">{p.progress}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {p.payment_status !== "keine" ? <PayBadge status={p.payment_status} /> : <span className="text-xs text-[#999]">—</span>}
                    {p.open_payment > 0 && <p className="mt-0.5 text-xs font-bold text-[#B88A4A]">{eur(p.open_payment)}</p>}
                  </td>
                  <td className="px-4 py-3">
                    {p.assignee_name ? <UserAvatar name={p.assignee_name} color={p.assignee_color} avatar={p.assignee_avatar} size={7} /> : <span className="text-xs text-[#999]">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ProjectForm open={formOpen} onOpenChange={setFormOpen} project={editing} clients={clients} users={users} onSaved={load} />
    </div>
  );
}
